'use client';

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Calendário mensal em português (dd/mm/aaaa). Resolve o problema do
 * `input type="date"` nativo, que herda o formato do navegador (mm/dd/aaaa).
 * Value/onChange usam ISO curto `yyyy-mm-dd`.
 */

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const paraDate = (iso: string): Date | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const paraIso = (d: Date): string => {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const ROTULO_DIA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' });

const mesmoDia = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Soma N dias ÚTEIS a partir de uma data (pula sábado e domingo). Feriados
 * dependem do backend (tabela de feriados) — aqui é só dia da semana. */
const somarDiasUteis = (base: Date, n: number): Date => {
  const d = new Date(base);
  let restantes = n;
  while (restantes > 0) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) restantes -= 1;
  }
  return d;
};

export interface AtalhoPrazo {
  label: string;
  diasUteis: number;
}

interface CalendarioBRProps {
  /** Data selecionada em ISO curto (yyyy-mm-dd) ou ''. */
  value: string;
  onChange: (iso: string) => void;
  /** Atalhos de prazo (ex.: 3/5/10 dias úteis). Calculados a partir de hoje. */
  atalhos?: AtalhoPrazo[];
  /**
   * Dias não úteis do calendário da câmara, `yyyy-mm-dd` → nome do feriado.
   * Quando informado, fins de semana e feriados ficam marcados (e continuam
   * selecionáveis: quem decide o prazo é a Secretaria).
   */
  diasNaoUteis?: Record<string, string>;
  /** Menor data selecionável em ISO curto. Dias anteriores ficam desabilitados. */
  minima?: string;
  /** Maior data selecionável em ISO curto. Dias posteriores ficam desabilitados. */
  maxima?: string;
  /** Notifica o mês exibido — quem consome usa para buscar o calendário do ano. */
  onMesVisivelChange?: (ano: number, mes: number) => void;
}

export function CalendarioBR({
  value,
  onChange,
  atalhos,
  diasNaoUteis,
  minima,
  maxima,
  onMesVisivelChange,
}: CalendarioBRProps) {
  const selecionada = paraDate(value);
  // TODO(#805): "hoje" no fuso do navegador diverge de `minima`/`maxima` em São Paulo.
  const hoje = new Date();
  const [visivel, setVisivel] = useState<Date>(() => {
    const base = selecionada ?? hoje;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const anoVisivel = visivel.getFullYear();
  const mesVisivel = visivel.getMonth();

  useEffect(() => {
    onMesVisivelChange?.(anoVisivel, mesVisivel);
  }, [anoVisivel, mesVisivel, onMesVisivelChange]);

  const escolherAtalho = (diasUteis: number) => {
    const alvo = somarDiasUteis(hoje, diasUteis);
    setVisivel(new Date(alvo.getFullYear(), alvo.getMonth(), 1));
    onChange(paraIso(alvo));
  };

  const rotuloMes = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
        .format(visivel)
        .replace(/^\w/, (c) => c.toUpperCase()),
    [visivel],
  );

  const dias = useMemo(() => {
    const primeiroDiaSemana = new Date(anoVisivel, mesVisivel, 1).getDay();
    const totalDias = new Date(anoVisivel, mesVisivel + 1, 0).getDate();
    const celulas: Array<Date | null> = [];
    for (let i = 0; i < primeiroDiaSemana; i += 1) celulas.push(null);
    for (let d = 1; d <= totalDias; d += 1) celulas.push(new Date(anoVisivel, mesVisivel, d));
    return celulas;
  }, [anoVisivel, mesVisivel]);

  const descreverNaoUtil = (dia: Date): string | null => {
    if (!diasNaoUteis) return null;
    const diaSemana = dia.getDay();
    if (diaSemana === 0 || diaSemana === 6) return 'fim de semana';
    return diasNaoUteis[paraIso(dia)] ?? null;
  };

  const irMes = (delta: number) =>
    setVisivel((atual) => new Date(atual.getFullYear(), atual.getMonth() + delta, 1));

  return (
    <div className="flex w-[268px] flex-col gap-2">
      {atalhos && atalhos.length > 0 ? (
        <div className="flex flex-col gap-1.5 border-b border-solid border-neutral-100 pb-2">
          <span className="text-subtext-color text-[11px] font-semibold tracking-[0.06em] uppercase">
            Atalhos
          </span>
          <div className="flex flex-wrap gap-1.5">
            {atalhos.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => escolherAtalho(a.diasUteis)}
                className="text-default-font hover:border-brand-300 hover:bg-brand-50 rounded-md border border-solid border-neutral-200 px-2 py-1 text-xs font-medium transition-colors"
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Mês anterior"
          onClick={() => irMes(-1)}
          className="text-subtext-color hover:text-default-font flex size-7 items-center justify-center rounded-md transition-colors hover:bg-neutral-100"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-body-bold font-body-bold text-default-font capitalize">
          {rotuloMes}
        </span>
        <button
          type="button"
          aria-label="Próximo mês"
          onClick={() => irMes(1)}
          className="text-subtext-color hover:text-default-font flex size-7 items-center justify-center rounded-md transition-colors hover:bg-neutral-100"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {DIAS_SEMANA.map((d, i) => (
          <div
            key={`${d}-${i}`}
            className="text-subtext-color flex h-7 items-center justify-center text-[11px] font-semibold"
          >
            {d}
          </div>
        ))}
        {dias.map((dia, i) => {
          if (!dia) return <div key={`vazio-${i}`} className="h-8" />;
          const iso = paraIso(dia);
          const selecionado = selecionada ? mesmoDia(dia, selecionada) : false;
          const ehHoje = mesmoDia(dia, hoje);
          const naoUtil = descreverNaoUtil(dia);
          const foraDaJanela =
            (minima !== undefined && iso < minima) || (maxima !== undefined && iso > maxima);
          const rotulo = ROTULO_DIA.format(dia);
          return (
            <button
              key={iso}
              type="button"
              disabled={foraDaJanela}
              title={naoUtil ?? undefined}
              aria-label={naoUtil ? `${rotulo} — ${naoUtil}` : rotulo}
              onClick={() => onChange(iso)}
              className={`flex h-8 items-center justify-center rounded-md text-sm transition-colors ${
                selecionado
                  ? 'bg-brand-600 font-semibold text-white'
                  : foraDaJanela
                    ? 'text-neutral-300'
                    : naoUtil
                      ? 'text-warning-700 bg-warning-100 hover:bg-neutral-100'
                      : `text-default-font hover:bg-neutral-100 ${ehHoje ? 'text-brand-700 font-semibold' : ''}`
              }`}
            >
              {dia.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
